import React from 'react';
import { IColumnTemplate } from '../../../Queries/AvailableColumnTemplatesHandler';
import { IItem } from '../../../../../../../../../../../../BaseComponents/Chip/DraggableChip/EDDraggableChipList';
import { IColumn } from '../../../../../../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { ekanalys_ekanalys_columns } from '../../../../../../../Queries/EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import { clone } from 'ramda';
import {
  getNewColumn,
  getEmptyColumn,
  newModalColumn,
} from '../../../EditColumn/Utils/EkonomiskAnalysEditColumnUtils';
import EkonomiskAnalysEditColumnDialog from '../../../EditColumn/Components/EkonomiskAnalysEditColumnDialog/EkonomiskAnalysEditColumnDialog';
import EkonomiskAnalysColumnCaptionQuery from '../../../../../../../Queries/EkonomiskAnalysColumnCaptionQuery/EkonomiskAnalysColumnCaptionQuery';
import EkonomiskAnalysColumnSelectContent from '../EkonomiskAnalysColumnSelectContent/EkonomiskAnalysColumnSelectContent';
import { IEkonomiskAnalysDates } from '../../../../../../AnalysSettingsHandler/AnalysSettingsHandler';
import {
  setVandTeckenIfResultColumn,
  addPrevYearCols,
  getColumnMaxId,
} from '../../Utils/EkonomiskAnalysColumnSelect.utils';
import { ISelectChoice } from '../../../../../../../../../../../../BaseComponents/Selects/SelectCommons';
import { DateInputType } from '../../../../../../../../../../../../Utils/dateUtils';

export enum ComparisonSetting {
  none,
  year,
}

interface IProps {
  selectedColumns: IColumn[];
  selectedTemplateColumns: IColumn[];
  availableColumnTemplates: IColumnTemplate[];
  codePrivilege: string;
  analysName: string;
  dates: IEkonomiskAnalysDates;
  dateInputType?: DateInputType;
  dateInvalid?: boolean;
  selectedColumnsUpdate: (columns: IColumn[]) => void;
  selectedTemplateColumnsUpdate: (columns: IColumn[]) => void;
  isInterval: boolean;
}

interface IState {
  selectedTemplate: string;
  selectedColumn: IColumn;
  newColumn?: boolean;
  newColumnModalOpen: boolean;
  localDates: IEkonomiskAnalysDates;
  prevYearCols: boolean;
  flipColumnSign: boolean;
  templateActionsOpen: boolean;
}

/**
 * En lista som fylls på med kolumner. Dessa väljs antingen enskilt, eller plocks in ifrån fördefinierade mallar.
 */
class EkonomiskAnalysColumnSelect extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = this.getInitialState();
  }

  getInitialState = (): IState => ({
    selectedTemplate: '',
    selectedColumn: newModalColumn(getEmptyColumn()),
    newColumn: undefined,
    newColumnModalOpen: false,
    localDates: this.props.dates,
    prevYearCols: false,
    flipColumnSign: false,
    templateActionsOpen: false,
  });

  setFlipColumnSign = (newValue: boolean) => {
    this.setState({ flipColumnSign: newValue });
  };

  setPrevYearCols = (newValue: boolean) => {
    this.setState({ prevYearCols: newValue });
  };

  changeTemplate = (templateId: ISelectChoice<string> | null) => {
    if (templateId == null) {
      this.setState(
        {
          selectedTemplate: '',
        },
        () => this.props.selectedTemplateColumnsUpdate([])
      );
      return;
    }

    let template = this.props.availableColumnTemplates.find(
      x => x.id === templateId.value
    );

    if (template) {
      if (this.state.prevYearCols) {
        template = {
          ...template,
          columns: addPrevYearCols(template.columns),
        };
      }

      if (this.state.flipColumnSign) {
        template = {
          ...template,
          columns: template.columns.map(setVandTeckenIfResultColumn(true)),
        };
      }
    }

    this.setState(
      {
        selectedTemplate: templateId.value,
      },
      () =>
        this.props.selectedTemplateColumnsUpdate(
          template ? template.columns : []
        )
    );
  };

  addColumns = (columns: IColumn[]) => {
    const maxId = getColumnMaxId(this.props.selectedColumns);
    columns.forEach((x, index) => (x.id = maxId + index + 1));

    this.props.selectedColumnsUpdate(
      this.props.selectedColumns.concat(columns)
    );
  };

  selectedColumnsUpdate = (items: IItem[]) => {
    const newSelectedColumns: IColumn[] = [];
    items.forEach(x => {
      const column = this.props.selectedColumns.find(y => y.id === x.id);

      if (column) {
        newSelectedColumns.push(column);
      }
    });
    this.props.selectedColumnsUpdate(newSelectedColumns);
  };

  selectedTemplateColumnsUpdate = (items: IItem[]) => {
    const newColumns: IColumn[] = [];
    items.forEach(x => {
      const column = this.props.selectedTemplateColumns.find(
        y => y.id === x.id
      );

      if (column) {
        newColumns.push(column);
      }
    });

    this.props.selectedTemplateColumnsUpdate(newColumns);
  };

  addTemplateColumns = () => {
    const addColumns = clone(this.props.selectedTemplateColumns);
    this.addColumns(addColumns);
    this.setState(
      {
        selectedTemplate: '',
      },
      () => {
        this.props.selectedTemplateColumnsUpdate([]);
      }
    );
  };

  addTemplateColumn = (index: number) => {
    const addColumn = clone(this.props.selectedTemplateColumns[index]);
    this.addColumns([addColumn]);
  };

  setColumn = (column: ekanalys_ekanalys_columns, newName: string) => {
    if (this.state.newColumn) {
      const newColumn: IColumn = {
        name: column.CODE_CAPTION || 'Namnlös kolumn',
        id: 0,
        data: column,
      };
      this.addColumns([newColumn]);
    } else {
      let newColumns = clone(this.props.selectedColumns);
      const index = this.props.selectedColumns.indexOf(
        this.state.selectedColumn
      );

      newColumns[index] = {
        name: newName,
        id: this.state.selectedColumn.id,
        data: column,
      };

      this.props.selectedColumnsUpdate(newColumns);
    }

    this.toggleNewColumnModal();
  };

  changeColumn = (column: IColumn) => {
    let newColumns = clone(this.props.selectedColumns);
    const index = this.props.selectedColumns.findIndex(
      col => col.id === column.id
    );

    if (index >= 0) {
      newColumns[index] = column;
      this.props.selectedColumnsUpdate(newColumns);
    }
  };

  selectedColumnClick = (column?: IColumn) => {
    this.setState(prevState => ({
      newColumnModalOpen: !prevState.newColumnModalOpen,
      selectedColumn: column ? column : newModalColumn(getNewColumn()),
      newColumn: !column,
    }));
  };

  toggleNewColumnModal = () => {
    this.setState(prevState => ({
      newColumnModalOpen: !prevState.newColumnModalOpen,
    }));
  };

  onExitNewColumnModal = () => {
    this.setState({
      selectedColumn: newModalColumn(getEmptyColumn()),
    });
  };

  onSelectTemplateActionsClick = () => {
    this.setState(prevState => ({
      templateActionsOpen: !prevState.templateActionsOpen,
    }));
  };

  render() {
    const { availableColumnTemplates, dates } = this.props;

    return (
      <>
        <EkonomiskAnalysColumnCaptionQuery
          onUpdate={this.props.selectedColumnsUpdate}
          columns={this.props.selectedColumns}
          codePrivilegeId={this.props.codePrivilege}
          analysName={this.props.analysName}
          dates={dates}
          isInterval={this.props.isInterval}
        >
          {() => (
            <EkonomiskAnalysColumnCaptionQuery
              onUpdate={this.props.selectedTemplateColumnsUpdate}
              columns={this.props.selectedTemplateColumns}
              codePrivilegeId={this.props.codePrivilege}
              analysName={this.props.analysName}
              dates={dates}
              isInterval={this.props.isInterval}
            >
              {() => (
                <EkonomiskAnalysColumnSelectContent
                  setSelectedColumns={this.props.selectedColumnsUpdate}
                  setSelectedTemplateColumns={
                    this.props.selectedTemplateColumnsUpdate
                  }
                  dates={this.props.dates}
                  dateInputType={
                    this.props.dateInputType != null
                      ? this.props.dateInputType
                      : DateInputType.year
                  }
                  selectedColumns={this.props.selectedColumns}
                  selectedTemplate={this.state.selectedTemplate}
                  availableColumnTemplates={availableColumnTemplates}
                  selectedTemplateColumns={this.props.selectedTemplateColumns}
                  hasCodePrivilege={this.props.codePrivilege !== ''}
                  changeTemplate={this.changeTemplate}
                  selectedColumnsUpdate={this.selectedColumnsUpdate}
                  selectedTemplateColumnsUpdate={
                    this.selectedTemplateColumnsUpdate
                  }
                  addTemplateColumns={this.addTemplateColumns}
                  addTemplateColumn={this.addTemplateColumn}
                  selectedColumnClick={this.selectedColumnClick}
                  selectionDisabled={this.props.dateInvalid}
                  selectionDisabledReason={
                    this.props.dateInvalid ? 'Ogiltig period' : undefined
                  }
                  isInterval={this.props.isInterval}
                  prevYearCols={this.state.prevYearCols}
                  setPrevYearCols={this.setPrevYearCols}
                  flipColumnSign={this.state.flipColumnSign}
                  setFlipColumnSign={this.setFlipColumnSign}
                  changeColumn={this.changeColumn}
                  templateActionsOpen={this.state.templateActionsOpen}
                  onSelectTemplateActionsClick={
                    this.onSelectTemplateActionsClick
                  }
                />
              )}
            </EkonomiskAnalysColumnCaptionQuery>
          )}
        </EkonomiskAnalysColumnCaptionQuery>
        <EkonomiskAnalysEditColumnDialog
          title="Ny kolumn"
          open={this.state.newColumnModalOpen}
          codePrivilegeId={this.props.codePrivilege}
          analysName={this.props.analysName}
          close={this.toggleNewColumnModal}
          onExited={this.onExitNewColumnModal}
          column={this.state.selectedColumn.data}
          setColum={this.setColumn}
          dates={dates}
          isInterval={this.props.isInterval}
          isNewColumn={Boolean(this.state.newColumn)}
        />
      </>
    );
  }
}

export default EkonomiskAnalysColumnSelect;
